import { z } from 'zod'
import { localeSchema, moneySchema, quantitySchema } from './common.js'

/**
 * Server PDF (01 §9, ADR-016, ADR-029): an issued document is an exact, append-only copy of what was
 * handed out, rendered by the worker from frozen data with the same React sheet as print and the
 * public page, stored through `files` with its SHA-256.
 */

export const DOCUMENT_TEMPLATE_KEYS = ['sales.invoice'] as const
export type DocumentTemplateKey = (typeof DOCUMENT_TEMPLATE_KEYS)[number]
export const documentTemplateKeySchema = z.enum(DOCUMENT_TEMPLATE_KEYS)

/** RENDERING → ISSUED (once) or FAILED; an ISSUED document may later be PURGED (the file, not the record). */
export const ISSUED_DOCUMENT_STATUSES = ['RENDERING', 'ISSUED', 'FAILED', 'PURGED'] as const
export type IssuedDocumentStatus = (typeof ISSUED_DOCUMENT_STATUSES)[number]

/** POST /sales/invoices/:id/pdf (and later templates): the language to issue in. */
export const issueDocumentRequestSchema = z.object({ language: localeSchema }).strict()
export type IssueDocumentRequest = z.input<typeof issueDocumentRequestSchema>

/** GET /documents?subjectType&subjectId. */
export const issuedDocumentListQuerySchema = z.object({
  subjectType: z
    .string()
    .trim()
    .regex(/^[a-z][a-z_]{0,39}$/),
  subjectId: z.uuid(),
})
export type IssuedDocumentListQuery = z.input<typeof issuedDocumentListQuerySchema>

/** GET /documents/:id/verify?sha256=… */
export const issuedDocumentVerifyQuerySchema = z.object({
  sha256: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[0-9a-f]{64}$/),
})
export type IssuedDocumentVerifyQuery = z.input<typeof issuedDocumentVerifyQuerySchema>

/** An issued document's record (GET /documents/:id/meta), as a DTO. */
export const issuedDocumentSchema = z.object({
  id: z.uuid(),
  templateKey: documentTemplateKeySchema,
  subjectType: z.string(),
  subjectId: z.uuid(),
  language: localeSchema,
  /** 1, 2, … per template, subject and language: a new version when the data changed. */
  version: z.number().int().min(1),
  status: z.enum(ISSUED_DOCUMENT_STATUSES),
  /** Hex SHA-256 of the PDF bytes; null until ISSUED. Kept after a purge as proof. */
  sha256: z.string().nullable(),
  size: z.number().int().nullable(),
  /** `<number>.pdf` (or the template's name) for downloads. */
  filename: z.string(),
  issuedAt: z.string(),
  issuedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  renderedAt: z.string().nullable(),
  failedReason: z.string().nullable(),
  purgedAt: z.string().nullable(),
})
export type IssuedDocument = z.output<typeof issuedDocumentSchema>

/** GET /documents/:id/verify. */
export interface IssuedDocumentVerification {
  id: string
  matches: boolean
}

// ── frozen data of a sales invoice (`sales.invoice`) ─────────────────────────

const text = (max: number) => z.string().max(max)
const nullableText = (max: number) => z.string().max(max).nullable()
const bilingual = z.object({ en: nullableText(10_000), ar: nullableText(10_000) }).strict()
/** An image the worker inlines as a data URL before rendering (never a URL the browser fetches). */
const imageRef = z.object({ fileId: z.uuid() }).strict()

const companySchema = z
  .object({
    name: text(200),
    nameAr: nullableText(200),
    legalName: nullableText(200),
    legalNameAr: nullableText(200),
    vatNumber: nullableText(50),
    crNumber: nullableText(50),
    address: nullableText(500),
    addressAr: nullableText(500),
    phone: nullableText(50),
    email: nullableText(254),
    logo: imageRef.nullable(),
  })
  .strict()

const lineSchema = z
  .object({
    lineNo: z.number().int().min(1),
    description: text(2000),
    quantity: quantitySchema,
    /** The unit a product line was sold in (ADR-045); absent on documents issued before units printed. */
    unit: z
      .object({ symbol: text(10), symbolAr: nullableText(10) })
      .strict()
      .nullable()
      .optional(),
    unitPrice: moneySchema,
    discountType: z.enum(['PERCENTAGE', 'PER_UNIT', 'LINE_AMOUNT']),
    discountValue: z.string().regex(/^-?\d{1,15}(\.\d{1,4})?$/),
    taxRate: z.string().regex(/^\d{1,3}(\.\d{1,4})?$/),
    discount: moneySchema,
    net: moneySchema,
    tax: moneySchema,
    total: moneySchema,
  })
  .strict()

const bankAccountSchema = z
  .object({
    name: text(200),
    nameAr: nullableText(200),
    bankName: nullableText(200),
    accountNumber: nullableText(100),
    iban: nullableText(50),
  })
  .strict()

/**
 * Everything the invoice sheet prints, frozen at issue: the same whitelist as the public page
 * (`PublicDocument`, ASM-042/064), so a PDF never shows internal data. Money is decimal strings only.
 */
export const salesInvoiceDocumentSchema = z
  .object({
    company: companySchema,
    document: z
      .object({
        kind: z.enum(['INVOICE', 'DEBIT_NOTE']),
        number: text(50),
        status: z.enum(['POSTED', 'VOID']),
        invoiceDate: z.iso.date(),
        dueDate: z.iso.date(),
        reference: nullableText(100),
      })
      .strict(),
    customer: z
      .object({
        code: text(50),
        name: text(200),
        nameAr: nullableText(200),
        taxNumber: nullableText(50),
        address: nullableText(1000),
      })
      .strict(),
    currency: z.string().length(3),
    pricesIncludeTax: z.boolean(),
    lines: z.array(lineSchema).min(1).max(1000),
    totals: z
      .object({
        gross: moneySchema,
        discount: moneySchema,
        subtotal: moneySchema,
        tax: moneySchema,
        total: moneySchema,
        amountReceived: moneySchema,
        amountCredited: moneySchema,
        balance: moneySchema,
        taxByRate: z.array(z.object({ rate: z.string(), net: moneySchema, tax: moneySchema }).strict()),
      })
      .strict(),
    notes: nullableText(5000),
    /** What the settings add to the sheet (`PrintExtras` on the web). */
    extras: z
      .object({
        footer: bilingual.nullable(),
        terms: bilingual.nullable(),
        paymentDetails: z
          .object({ bankAccounts: z.array(bankAccountSchema).max(10), text: bilingual })
          .strict()
          .nullable(),
        signature: imageRef.nullable(),
        stamp: imageRef.nullable(),
      })
      .strict(),
  })
  .strict()
export type SalesInvoiceDocument = z.output<typeof salesInvoiceDocumentSchema>

/** The frozen-data schema of each template key. */
export const DOCUMENT_DATA_SCHEMAS = {
  'sales.invoice': salesInvoiceDocumentSchema,
} as const satisfies Record<DocumentTemplateKey, z.ZodType>

// ── the render page's input (`window.__SAHAB_RENDER__`, PD3/PD4) ─────────────

/** An image inlined by the worker: a base64 data URL of a raster image, never a URL to fetch. */
export const renderImageDataUrlSchema = z
  .string()
  .max(14_000_000)
  .regex(/^data:image\/(png|jpeg|gif|webp);base64,[A-Za-z0-9+/]+={0,2}$/)

/**
 * What the worker hands the render page: the template, the language, the frozen data (validated by
 * the page against `DOCUMENT_DATA_SCHEMAS[templateKey]`) and every image the data names by
 * `{fileId}`, inlined as a data URL. The page fetches nothing.
 */
export const documentRenderInputSchema = z
  .object({
    templateKey: documentTemplateKeySchema,
    language: localeSchema,
    data: z.unknown(),
    images: z.record(z.uuid(), renderImageDataUrlSchema),
  })
  .strict()
export type DocumentRenderInput = z.input<typeof documentRenderInputSchema>
